import React, { useMemo, useRef, useState } from 'react';
import useFetch from 'src/components/leases/useLeaseRecordsFetch';
import { ILeaseMaster, ILeaseTransaction } from 'src/models/dlcLease/ILeaseMaster';
import * as styles from './styles';
import LeaseSsrsReportDialog from './LeaseSsrsReportDialog';
import useLatestRequest from '../useLatestRequest';

interface ITransactionsSectionProps {
    leaseMaster: ILeaseMaster;
    onTransactionsRefresh?: (transactions: ILeaseTransaction[]) => void;
}

export const getTransactionRowKey = (transaction: ILeaseTransaction): string =>
    JSON.stringify([
        transaction.leaseNo,
        transaction.tranType,
        transaction.tranNo,
        transaction.tranDate,
        transaction.dscr,
        transaction.amount,
        transaction.arCol,
        transaction.apCol,
    ]);

const TransactionsSection: React.FC<ITransactionsSectionProps> = ({ leaseMaster, onTransactionsRefresh }) => {
    const { post, response } = useFetch();
    const refreshRequest = useLatestRequest();
    const refreshInProgressRef = useRef(false);
    const [isRefreshingAr, setIsRefreshingAr] = useState(false);
    const [refreshNotice, setRefreshNotice] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
    const transactions = useMemo(
        () =>
            [...(leaseMaster.transactions || [])].sort((left, right) => {
                const leftTime = new Date(left.tranDate).getTime();
                const rightTime = new Date(right.tranDate).getTime();
                const normalizedLeftTime = Number.isNaN(leftTime) ? Number.MAX_SAFE_INTEGER : leftTime;
                const normalizedRightTime = Number.isNaN(rightTime) ? Number.MAX_SAFE_INTEGER : rightTime;

                return normalizedLeftTime - normalizedRightTime || (left.tranNo || '').localeCompare(right.tranNo || '');
            }),
        [leaseMaster.transactions]
    );
    const transactionRows = useMemo(() => {
        const keyOccurrences = new Map<string, number>();

        return transactions.map((transaction) => {
            const baseKey = getTransactionRowKey(transaction);
            const occurrence = keyOccurrences.get(baseKey) || 0;
            keyOccurrences.set(baseKey, occurrence + 1);

            return { transaction, key: `${baseKey}-${occurrence}` };
        });
    }, [transactions]);
    const [isLeaseTransactionsReportDialogOpen, setIsLeaseTransactionsReportDialogOpen] = useState(false);
    const [isLeaseNonZeroBalanceReportDialogOpen, setIsLeaseNonZeroBalanceReportDialogOpen] = useState(false);
    const leaseNo = leaseMaster.lsNo ? leaseMaster.lsNo.toString() : '';
    const clientNo = leaseMaster.clNo ? leaseMaster.clNo.toString() : '';
    const projectNo = leaseMaster.prNo ? leaseMaster.prNo.toString() : '';
    const hasValidLease = typeof leaseMaster.lsNo === 'number' && Number.isFinite(leaseMaster.lsNo) && leaseMaster.lsNo > 0;

    const refreshAr = async (): Promise<void> => {
        if (!hasValidLease || refreshInProgressRef.current) {
            return;
        }

        refreshInProgressRef.current = true;
        const requestId = refreshRequest.begin();
        setIsRefreshingAr(true);
        setRefreshNotice(null);

        try {
            const updatedTransactions = await post(`/v1.0/leases/master/${leaseMaster.lsNo}/transactions/sync-ar`);
            if (!refreshRequest.isCurrent(requestId)) {
                return;
            }
            if (!response.ok || !Array.isArray(updatedTransactions)) {
                throw new Error('A/R synchronization failed.');
            }

            if (onTransactionsRefresh) {
                onTransactionsRefresh(updatedTransactions as ILeaseTransaction[]);
            }
            setRefreshNotice({ type: 'success', message: 'A/R transactions refreshed successfully.' });
        } catch {
            if (refreshRequest.isCurrent(requestId)) {
                setRefreshNotice({ type: 'error', message: 'Unable to refresh A/R transactions. Please try again.' });
            }
        } finally {
            if (refreshRequest.isCurrent(requestId)) {
                refreshInProgressRef.current = false;
                setIsRefreshingAr(false);
            }
        }
    };

    const formatCurrency = (amount?: number): string => {
        if (amount === undefined || amount === null) {
            return '-';
        }

        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
        }).format(amount);
    };

    const formatDate = (date?: Date | string): string => {
        if (!date) {
            return '-';
        }

        return new Intl.DateTimeFormat('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        }).format(new Date(date));
    };

    const balanceCellStyle = (amount?: number): React.CSSProperties => {
        if (!amount) {
            return {
                ...styles.tableCell,
                textAlign: 'right',
                fontWeight: styles.typography.fontWeight.semibold,
            };
        }

        const isPositive = amount > 0;
        return {
            ...styles.tableCell,
            textAlign: 'right',
            fontWeight: styles.typography.fontWeight.semibold,
            backgroundColor: isPositive ? '#22c55e' : '#ef4444',
            color: isPositive ? styles.colors.gray900 : styles.colors.white,
        };
    };

    return (
        <div style={styles.sectionContainer}>
            <h2 style={styles.sectionHeader}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={styles.colors.primary} strokeWidth="2">
                        <path d="M3 3h18v18H3zM12 8v8m4-4H8" />
                        <path d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        <path d="M12 6v6l4 2" />
                    </svg>
                    Transactions
                </span>
                {transactions.length > 0 && (
                    <span style={{ fontSize: styles.typography.fontSize.sm, color: styles.colors.gray600 }}>
                        {transactions.length} transaction{transactions.length !== 1 ? 's' : ''}
                    </span>
                )}
            </h2>

            <div style={reportActionsStyle}>
                <button
                    type="button"
                    style={{ ...reportButtonStyle, ...(isRefreshingAr || !hasValidLease ? disabledButtonStyle : {}) }}
                    onClick={refreshAr}
                    disabled={isRefreshingAr || !hasValidLease}
                    aria-busy={isRefreshingAr}
                >
                    {isRefreshingAr ? 'Refreshing A/R…' : 'Refresh A/R'}
                </button>
                <button type="button" style={reportButtonStyle} onClick={() => setIsLeaseTransactionsReportDialogOpen(true)}>
                    Lease Transaction Report
                </button>
                <button type="button" style={reportButtonStyle} onClick={() => setIsLeaseNonZeroBalanceReportDialogOpen(true)}>
                    Lease Non-Zero Balance Report
                </button>
            </div>

            {refreshNotice && (
                <div
                    role={refreshNotice.type === 'error' ? 'alert' : 'status'}
                    aria-live="polite"
                    style={refreshNotice.type === 'error' ? errorNoticeStyle : successNoticeStyle}
                >
                    {refreshNotice.message}
                </div>
            )}

            {transactions.length === 0 ? (
                <div style={styles.emptyState}>
                    <svg
                        width="48"
                        height="48"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke={styles.colors.gray400}
                        strokeWidth="1.5"
                        style={{ margin: '0 auto 1rem' }}
                    >
                        <rect x="3" y="7" width="18" height="12" rx="2" />
                        <path d="M3 10h18" />
                        <path d="M7 15h2" />
                        <path d="M15 15h2" />
                    </svg>
                    <p style={{ margin: 0, color: styles.colors.gray600, fontSize: styles.typography.fontSize.lg }}>No transactions recorded</p>
                    <p style={{ marginTop: '0.5rem', fontSize: styles.typography.fontSize.sm, color: styles.colors.gray500 }}>
                        Transaction history will appear here once available
                    </p>
                </div>
            ) : (
                <div style={styles.tableContainer}>
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={styles.tableHeader}>Transaction Type</th>
                                <th style={styles.tableHeader}>Transaction Number</th>
                                <th style={styles.tableHeader}>Trans. Date</th>
                                <th style={styles.tableHeader}>Transaction Description</th>
                                <th style={{ ...styles.tableHeader, textAlign: 'right' }}>Amount</th>
                                <th style={{ ...styles.tableHeader, textAlign: 'right' }}>Accts Receivable</th>
                                <th style={{ ...styles.tableHeader, textAlign: 'right' }}>Accts Payable</th>
                            </tr>
                        </thead>
                        <tbody>
                            {transactionRows.map(({ transaction, key }) => (
                                <tr key={key} style={styles.tableRow}>
                                    <td style={styles.tableCell}>
                                        <span
                                            style={{
                                                ...styles.badge,
                                                backgroundColor: styles.colors.bgSecondary,
                                                color: styles.colors.primary,
                                            }}
                                        >
                                            {transaction.tranType}
                                        </span>
                                    </td>
                                    <td style={styles.tableCell}>{transaction.tranNo || '-'}</td>
                                    <td style={styles.tableCell}>{formatDate(transaction.tranDate)}</td>
                                    <td style={styles.tableCell}>{transaction.dscr}</td>
                                    <td style={{ ...styles.tableCell, textAlign: 'right', fontWeight: styles.typography.fontWeight.semibold }}>
                                        {formatCurrency(transaction.amount)}
                                    </td>
                                    <td style={balanceCellStyle(transaction.arCol)}>{formatCurrency(transaction.arCol)}</td>
                                    <td style={balanceCellStyle(transaction.apCol)}>{formatCurrency(transaction.apCol)}</td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr>
                                <td colSpan={5} style={transactionTotalsSpacerStyle} />
                                <td style={transactionTotalCellStyle}>
                                    <span style={transactionTotalLabelStyle}>Receivables Total</span>
                                    <span style={transactionTotalValueStyle}>
                                        {formatCurrency(transactions.reduce((sum, transaction) => sum + (transaction.arCol || 0), 0))}
                                    </span>
                                </td>
                                <td style={transactionTotalCellStyle}>
                                    <span style={transactionTotalLabelStyle}>Payables Total</span>
                                    <span style={transactionTotalValueStyle}>
                                        {formatCurrency(transactions.reduce((sum, transaction) => sum + (transaction.apCol || 0), 0))}
                                    </span>
                                </td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            )}

            <LeaseSsrsReportDialog
                isOpen={isLeaseTransactionsReportDialogOpen}
                reportType="transactions"
                onClose={() => setIsLeaseTransactionsReportDialogOpen(false)}
                initialLeaseNo={leaseNo}
                initialClientNo={clientNo}
                initialProjectNo={projectNo}
            />
            <LeaseSsrsReportDialog
                isOpen={isLeaseNonZeroBalanceReportDialogOpen}
                reportType="nonZeroBalance"
                onClose={() => setIsLeaseNonZeroBalanceReportDialogOpen(false)}
                initialClientNo={clientNo}
                initialProjectNo={projectNo}
            />
        </div>
    );
};

const reportActionsStyle: React.CSSProperties = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.75rem',
    marginBottom: '1rem',
};

const reportButtonStyle: React.CSSProperties = {
    ...styles.buttonSecondary,
    height: '34px',
    padding: '0 0.85rem',
    fontSize: styles.typography.fontSize.xs,
    fontWeight: styles.typography.fontWeight.semibold,
};

const disabledButtonStyle: React.CSSProperties = {
    cursor: 'not-allowed',
    opacity: 0.55,
};

const noticeStyle: React.CSSProperties = {
    marginBottom: '1rem',
    padding: '0.65rem 0.85rem',
    borderRadius: '4px',
    fontSize: styles.typography.fontSize.sm,
};

const successNoticeStyle: React.CSSProperties = {
    ...noticeStyle,
    border: '1px solid #86efac',
    backgroundColor: '#f0fdf4',
    color: '#166534',
};

const errorNoticeStyle: React.CSSProperties = {
    ...noticeStyle,
    border: '1px solid #fca5a5',
    backgroundColor: '#fef2f2',
    color: '#991b1b',
};

const transactionTotalsSpacerStyle: React.CSSProperties = {
    borderTop: `2px solid ${styles.colors.gray200}`,
};

const transactionTotalCellStyle: React.CSSProperties = {
    ...styles.tableCell,
    paddingTop: '0.75rem',
    paddingBottom: '0.75rem',
    textAlign: 'right',
    backgroundColor: styles.colors.gray50,
    borderTop: `2px solid ${styles.colors.gray200}`,
};

const transactionTotalLabelStyle: React.CSSProperties = {
    display: 'block',
    marginBottom: '0.25rem',
    color: styles.colors.gray600,
    fontSize: styles.typography.fontSize.sm,
};

const transactionTotalValueStyle: React.CSSProperties = {
    display: 'block',
    color: styles.colors.gray900,
    fontSize: styles.typography.fontSize.lg,
    fontWeight: styles.typography.fontWeight.bold,
};

export default TransactionsSection;



// WEBPACK FOOTER //
// ./src/components/leases/sections/Transactions.tsx